import React from "react";
import { Link } from "react-router-dom";

const Home = () => {
  return (
    <main className="relative">
      {/* Decorative background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[1100px] rounded-full bg-gradient-to-r from-purple-200 via-fuchsia-200 to-indigo-200 blur-3xl opacity-60" />
        <svg className="absolute bottom-0 left-0 w-full text-gray-50" viewBox="0 0 1440 180" preserveAspectRatio="none">
          <path fill="currentColor" d="M0,96L60,117.3C120,139,240,181,360,181.3C480,181,600,139,720,133.3C840,128,960,160,1080,170.7C1200,181,1320,171,1380,165.3L1440,160L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <section className="pt-14 sm:pt-20">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-4 py-2 ring-1 ring-purple-200 mb-6">
              <span className="text-xs font-semibold text-purple-700 tracking-wide">New</span>
              <Link to="/dashboard" className="text-xs text-gray-700 hover:text-gray-900 inline-flex items-center gap-1">
                Team dashboard preview
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold tracking-tight text-gray-900">
              Design. Remix. Ship.
            </h1>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              A creative toolkit of beautiful, reusable components. Preview live, copy the code, and build
              delightful interfaces faster.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link to="/components" className="inline-flex items-center gap-2 rounded-md bg-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-purple-700">
                Explore Components
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>
              <Link to="/register" className="inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-purple-700 ring-1 ring-purple-200 hover:bg-purple-50">
                Get Started Free
              </Link>
            </div>
            {/* Showcase mosaic */}
            <div className="mt-12 grid grid-cols-3 sm:grid-cols-6 gap-3">
              {["Buttons","Inputs","Cards","Forms","Tabs","Badges"].map((label) => (
                <div key={label} className="aspect-[3/2] rounded-xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 grid place-items-center text-xs sm:text-sm text-gray-600">
                  {label}
                </div>
              ))}
            </div>
            {/* Stats */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 text-left sm:text-center">
              {[
                {k:"Components", v:"150+"},
                {k:"Categories", v:"12"},
                {k:"Copy & paste", v:"Instant"},
                {k:"Private libs", v:"Yes"},
              ].map((s) => (
                <div key={s.k} className="rounded-lg bg-white/70 backdrop-blur ring-1 ring-gray-100 p-4">
                  <div className="text-2xl font-bold text-gray-900">{s.v}</div>
                  <div className="text-xs text-gray-500">{s.k}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 sm:py-24">
          <h2 className="text-3xl font-bold text-gray-900 text-center">Build faster with ModStack</h2>
          <p className="text-base text-gray-600 text-center mt-3 max-w-2xl mx-auto">
            Production-ready patterns you can drop into any stack. No lock‑in, just clean HTML, CSS, and JS.
          </p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Reusable UI",
                desc: "Save time with copy‑paste building blocks.",
                icon: (
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                )
              },
              {
                title: "Component Library",
                desc: "Curated set across buttons, forms, and more.",
                icon: (
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>
                )
              },
              {
                title: "Open Library",
                desc: "Browse public components from the community.",
                icon: (
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>
                )
              },
              {
                title: "Easy Integration",
                desc: "Drop into React, Vue, or plain HTML projects.",
                icon: (
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                )
              }
            ].map((f) => (
              <div key={f.title} className="p-6 rounded-xl bg-white/80 backdrop-blur ring-1 ring-gray-100 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 grid place-items-center mb-3">
                  {f.icon}
                </div>
                <div className="text-gray-900 font-semibold">{f.title}</div>
                <div className="text-sm text-gray-600 mt-1">{f.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="pb-20">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600">
            <div className="absolute inset-0 opacity-20">
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 600 600" fill="none">
                <g opacity=".5" stroke="white"><circle cx="300" cy="300" r="180" /><circle cx="300" cy="300" r="250" /></g>
              </svg>
            </div>
            <div className="relative px-8 py-12 sm:px-12 sm:py-14 lg:px-16 lg:py-16 text-white grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold">Start creating today</h3>
                <p className="mt-2 text-white/90">Sign up for free and build your private component library.</p>
              </div>
              <div className="flex gap-3 lg:justify-end">
                <Link to="/register" className="inline-flex items-center gap-2 rounded-md bg-white text-gray-900 px-4 py-2 text-sm font-semibold shadow-sm hover:bg-gray-100">Get Started</Link>
                <Link to="/components" className="inline-flex items-center gap-2 rounded-md ring-1 ring-white/60 px-4 py-2 text-sm font-semibold text-white/95 hover:bg-white/10">Browse Components</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Home;
